/**
 * ============================================================================
 * Shipment Landed Cost — Adapter (v3, server-driven)
 * ============================================================================
 *
 * ENG-01 Round 3 — La matemática vive ahora en PostgreSQL
 * (`calculate_landed_cost_preview`). Este archivo expone DOS APIs:
 *
 *   ▸ `useShipmentLandedCost(args)`  ← NUEVO. React Query hook async que
 *      proxy al RPC, con loading/error states, debounce nativo (300ms) y
 *      adaptador que mantiene el shape legacy `ShipmentLandedResult` para
 *      que los consumidores apenas cambien.
 *
 *   ▸ `calcShipmentLandedCost(...)`  ← @deprecated. Sync wrapper sobre el
 *      motor TypeScript canónico (legacy). Sigue activo SOLO mientras
 *      migramos componente por componente. NO añadir nuevos consumidores.
 *
 * Migration note: legacy fields `fleteAsignado` / `seguroAsignado` /
 * `otrosAsignados` are kept as **aliases** of v2 pools so the Desglose
 * de Prorrateo tooltip in `InventoryTable.tsx` keeps rendering.
 * ============================================================================
 */

import { useMemo } from "react";
import {
  calcLandedCostV2,
  type CostEngineInput,
  type CostEngineResult,
  type ComplianceRates,
  type EngineDocumentInput,
  type EngineGastoInput,
  type EngineItemInput,
  type ProrationBase,
  type TRMMap as EngineTRMMap,
} from "@/lib/landedCostEngine";
import {
  useLandedCostPreview,
  type LandedCostPreviewInput,
  type LandedCostPreviewResult,
} from "@/hooks/useLandedCostPreview";
import { supabase } from "@/integrations/supabase/client";
import { getMontoCapitalizable } from "@/hooks/useGastosEmbarque";

// ── Re-exports for backward compatibility ───────────────────────────────────
export type TRMMap = EngineTRMMap;

export interface ShipmentDocument extends EngineDocumentInput {}
export interface ShipmentItem extends EngineItemInput {}

/** Legacy gasto shape used by EmbarqueDetailModal. */
export interface ShipmentGasto {
  id: string;
  concepto: string;
  /** Total bruto pagado (Base + IVA). Conservado para auditoría/reconciliación. */
  monto: number;
  /** Base capitalizable (sin IVA). Si está presente y > 0, el motor la prioriza. */
  monto_base?: number | null;
  moneda: string;
  base_prorrateo: ProrationBase;
  trm_pago?: number | null;
  aplica_costeo_inventario?: boolean;
  /** DIAN — entra a base gravable (CIF aduanero). Default false / null=autodetect. */
  incluir_en_base_gravable?: boolean | null;
}

// ── Result shape kept BACKWARD-COMPATIBLE with v1 consumers ────────────────
export interface ItemProration {
  itemId: string;
  monedaOrigen: string;
  factorValor: number;
  factorPeso: number;
  /** Prorrateo por base Peso (ex-flete). Alias for v2 gastosPesoAsignadosCOP. */
  fleteAsignado: number;
  /** Mitad informativa del prorrateo por Valor (alias para UI). */
  seguroAsignado: number;
  otrosAsignados: number;
  /** v2 native fields. */
  gastosPesoAsignados: number;
  gastosValorAsignados: number;
  cifItemCOP: number;
  arancelItemCOP: number;
  ivaItemCOP: number;
  landedCostTotalCOP: number;
  landedCostUnitarioCOP: number;
  /** % arancel efectivo aplicado por el RPC (post-beneficio). Fuente única para UI/dossier. */
  arancelPctEfectivo?: number;
  /** % IVA efectivo aplicado por el RPC (post-beneficio + override). */
  ivaPctEfectivo?: number;
  /** ICO — Impuesto al Consumo total del ítem en COP (capitaliza al landed cost). */
  icoCOP?: number;
  /** Tipo de ICO aplicado por el RPC: 'BEBIDAS_ALCOHOLICAS' | 'VEHICULOS' | 'CIGARRILLOS' | null. */
  icoTipo?: string | null;
}

export interface GlobalTotals {
  fobTotalCOP: number;
  pesoTotalKg: number;
  /** Pool agregado por Peso (incluye legacy costo_flete). */
  gastosPesoCOP: number;
  /** Pool agregado por Valor (incluye legacy seguro + otros_cargos). */
  gastosValorCOP: number;
  /** Fase C: pool nativo por CBM (separado del de Valor). 0 si no hay gastos CBM. */
  gastosCBMCOP: number;
}

/**
 * TRACE-01 — Allocation atómica (gasto × ítem) en COP.
 * Convierte el shape RAW del RPC al shape camelCase para consumidores UI.
 */
export interface AllocationLine {
  gastoId: string | null;
  itemId: string;
  montoCOP: number;
  baseDeclarada: "CBM" | "Peso" | "Valor";
  /** Fase C: 'CBM' es ahora una base efectiva nativa del RPC. */
  baseEfectiva: "Peso" | "Valor" | "CBM";
}

export interface ShipmentLandedResult {
  globals: GlobalTotals;
  items: ItemProration[];
  totalLandedCOP: number;
  reconciliationDriftCOP: number;
  engineVersion: "v1" | "v2";
  warningFallbackPesoActivado: boolean;
  /** Hay al menos un ítem sin peso → toda la bolsa de Peso fue por Valor. */
  warningPesoIncompleto: boolean;
  /** Lista de itemIds sin peso, para listar en el banner UI. */
  itemsSinPeso: string[];
  /** true si el cálculo usó TRM histórica (sin DIM). false si usó TRM Aduana oficial. */
  isEstimated?: boolean;
  /** 'aduana' (final) o 'mercado' (estimado). */
  trmSource?: "aduana" | "mercado";
  /** TRACE-01 — desglose por (gasto × ítem). Vacío en API legacy sync. */
  allocations: AllocationLine[];
}

// ─────────────────────────────────────────────────────────────────────────────
// HOOK ASYNC (server-driven) — primary API as of ENG-01
// ─────────────────────────────────────────────────────────────────────────────

export interface UseShipmentLandedCostArgs {
  documents: ShipmentDocument[];
  items: ShipmentItem[];
  /** Beneficio aduanero por itemId (para resolver `aplica_tlc` legacy + matrix). */
  beneficioByItemId?: Record<string, "GENERAL" | "TLC_PURO" | "LEY_1715" | "ZONA_FRANCA">;
  gastos: ShipmentGasto[];
  trmAduana?: number | null;
  /** Disable while parent data is loading. */
  enabled?: boolean;
  /** Debounce override (ms). Default 300. */
  debounceMs?: number;
}

export interface UseShipmentLandedCostReturn {
  data: ShipmentLandedResult | null;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  /** Raw RPC payload for debugging. */
  rawResult: LandedCostPreviewResult | undefined;
}

/**
 * React Query hook that drives Landed Cost via the server-side RPC.
 * Returns the same `ShipmentLandedResult` shape as the legacy sync engine
 * so consumers only swap the call site.
 */
export function useShipmentLandedCost(args: UseShipmentLandedCostArgs): UseShipmentLandedCostReturn {
  const { documents, items, gastos, trmAduana, beneficioByItemId, enabled = true, debounceMs = 300 } = args;

  // ── Build raw input for the RPC (server enriches arancel/iva from arancel_1881
  //    and trm_map from trm_historico). No client-side compliance map needed. ──
  const rawInput: LandedCostPreviewInput | null = useMemo(() => {
    if (!items.length) return null;

    const docMonedaById = new Map(documents.map((d) => [d.id, (d.moneda ?? "COP").toString()]));

    return {
      // Si no hay TRM Aduana, el RPC hará fallback a TRM histórica USD (modo ESTIMADO).
      trm_aduana: trmAduana && trmAduana > 0 ? Number(trmAduana) : (null as unknown as number),
      items: items.map((it) => {
        const beneficio = beneficioByItemId?.[it.id]
          ?? (it.aplica_tlc ? "TLC_PURO" : "GENERAL");
        return {
          id: it.id,
          documento_id: it.documento_id,
          moneda_doc: (docMonedaById.get(it.documento_id) ?? "COP").toUpperCase(),
          cantidad: Number(it.cantidad ?? 0),
          peso_bruto_kg: Number(it.peso_bruto_kg ?? 0),
          sub_total: Number(it.sub_total ?? 0),
          hs_code: (it as { hs_code?: string | null }).hs_code ?? null,
          aplica_tlc: !!it.aplica_tlc,
          iva_override: it.iva_override != null ? Number(it.iva_override) : null,
          beneficio_aduanero: beneficio,
        };
      }),
      // CRÍTICO B-02A: enviar `monto_base` (sin IVA) como `monto` capitalizable.
      // Si llega null/0, capitalizamos 0 — más seguro que inflar inventario con bruto.
      // CRÍTICO B-02A: enviar `monto_base` (sin IVA) como `monto` capitalizable.
      // Si llega null/0, capitalizamos 0 — más seguro que inflar inventario con bruto.
      // TRACE-01: enviamos también `id` y `base_declarada` para que el RPC devuelva
      // allocations correlacionables y el desglose UI respete CBM como etiqueta.
      gastos: gastos.map((g) => ({
        id: g.id,
        // FIX Fase A — usar helper canónico: si monto_base es null/0 (gastos COP
        // manuales sin desglose IVA), cae al monto bruto. Antes capitalizábamos 0
        // y el resumen mostraba "Gastos por Valor: $0" pese a tener gastos válidos.
        monto: getMontoCapitalizable(g),
        moneda: g.moneda,
        // Fase C — CBM es ahora una bolsa nativa del RPC. Ya NO se colapsa a Valor.
        base_prorrateo: g.base_prorrateo,
        base_declarada: g.base_prorrateo,
        trm_pago: g.trm_pago ?? null,
        aplica_costeo_inventario: g.aplica_costeo_inventario ?? true,
      })),
    };
  }, [documents, items, gastos, trmAduana, beneficioByItemId]);

  const query = useLandedCostPreview(rawInput, { debounceMs, enabled });

  const adapted = useMemo<ShipmentLandedResult | null>(() => {
    if (!query.data) return null;
    return adaptRpcToLegacyShape(query.data, items);
  }, [query.data, items]);

  return {
    data: adapted,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: (query.error as Error | null) ?? null,
    rawResult: query.data,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Adapter: RPC JSONB shape → legacy ShipmentLandedResult shape
// ─────────────────────────────────────────────────────────────────────────────

function adaptRpcToLegacyShape(
  rpc: LandedCostPreviewResult,
  items: ShipmentItem[],
): ShipmentLandedResult {
  const itemsMeta = new Map(items.map((it) => [it.id, it]));
  const fobTotalCOP = Number(rpc.fob_total_cop ?? 0);

  const itemsSinPeso = items
    .filter((it) => !Number(it.peso_bruto_kg ?? 0))
    .map((it) => it.id);

  const legacyItems: ItemProration[] = (rpc.items ?? []).map((r) => {
    const meta = itemsMeta.get(r.id);
    const monedaOrigen = ""; // no longer surfaced by the RPC; UI uses moneda from doc.
    const subCop = meta ? Number(meta.sub_total ?? 0) * (rpc.trm_aduana_usada ?? 1) : 0;
    const factorValor = fobTotalCOP > 0 ? subCop / fobTotalCOP : 0;
    const factorPeso = factorValor; // server already handled peso-zero fallback
    const cif = Number(r.cif_cop);
    const allocPeso = Number(r.alloc_peso_cop);
    const allocValor = Number(r.alloc_valor_cop);
    // Fase C — la bolsa CBM se contabiliza aparte en el RPC. Para mantener el
    // shape legacy (consumidores antiguos esperan que `gastosValorAsignados`
    // contenga TODO lo no-peso), sumamos CBM al campo legacy. La discriminación
    // viva queda en `globals.gastosCBMCOP` + en `allocations[].baseEfectiva`.
    const allocCBM = Number((r as any).alloc_cbm_cop ?? 0);

    return {
      itemId: r.id,
      monedaOrigen,
      factorValor,
      factorPeso,
      fleteAsignado: allocPeso,
      seguroAsignado: allocValor + allocCBM,
      otrosAsignados: 0,
      gastosPesoAsignados: allocPeso,
      gastosValorAsignados: allocValor + allocCBM,
      cifItemCOP: cif,
      arancelItemCOP: Number(r.arancel_cop),
      ivaItemCOP: Number(r.iva_cop),
      landedCostTotalCOP: Number(r.landed_total_cop),
      landedCostUnitarioCOP: Number(r.landed_unit_cop),
      arancelPctEfectivo: r.arancel_pct != null ? Number(r.arancel_pct) : undefined,
      ivaPctEfectivo: r.iva_pct != null ? Number(r.iva_pct) : undefined,
      icoCOP: (r as any).ico_cop != null ? Number((r as any).ico_cop) : undefined,
      icoTipo: (r as any).ico_tipo ?? null,
    };
  });

  const totalLandedCOP = legacyItems.reduce((acc, r) => acc + r.landedCostTotalCOP, 0);

  const trmSource = (rpc.trm_source ?? "aduana") as "aduana" | "mercado";

  // TRACE-01: convertir allocations RAW (snake_case) → camelCase para UI.
  const allocations: AllocationLine[] = (((rpc as any).allocations ?? []) as Array<{
    gasto_id: string | null;
    item_id: string;
    monto_cop: number | string;
    base_declarada: "CBM" | "Peso" | "Valor";
    base_efectiva: "Peso" | "Valor";
  }>).map((a) => ({
    gastoId: a.gasto_id,
    itemId: a.item_id,
    montoCOP: Number(a.monto_cop ?? 0),
    baseDeclarada: a.base_declarada,
    baseEfectiva: a.base_efectiva,
  }));

  return {
    engineVersion: "v2",
    reconciliationDriftCOP: 0, // RPC reconciles via Largest Remainder — drift is always 0
    warningFallbackPesoActivado: Number(rpc.peso_total_kg ?? 0) <= 0,
    warningPesoIncompleto: itemsSinPeso.length > 0 && itemsSinPeso.length < items.length,
    itemsSinPeso,
    isEstimated: trmSource === "mercado",
    trmSource,
    totalLandedCOP,
    globals: {
      fobTotalCOP,
      pesoTotalKg: Number(rpc.peso_total_kg ?? 0),
      gastosPesoCOP: Number(rpc.gastos_peso_cop ?? 0),
      gastosValorCOP: Number(rpc.gastos_valor_cop ?? 0),
      // Fase C — bolsa CBM nativa devuelta por el RPC. 0 si no hay gastos CBM.
      gastosCBMCOP: Number((rpc as any).gastos_cbm_cop ?? 0),
    },
    items: legacyItems,
    allocations,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// LEGACY SYNC API (DEPRECATED) — kept alive while we migrate consumers.
// `InventoryTable.tsx` (single-doc scope, no gastos) still uses it. New code
// MUST use `useShipmentLandedCost`.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @deprecated ENG-01 (2026-04-30) — Use the React Query hook
 *   `useShipmentLandedCost(args)` instead. This sync wrapper still works
 *   as a safety net for legacy single-document call sites (e.g.
 *   `InventoryTable` parent-doc preview), but the canonical math is now
 *   the Postgres RPC `calculate_landed_cost_preview`.
 */
export function calcShipmentLandedCost(
  documents: ShipmentDocument[],
  items: ShipmentItem[],
  trmMap: TRMMap,
  arancelByItemId: Record<string, number>,
  gastos: ShipmentGasto[] = [],
  trmAduana?: number | null,
  ivaByItemId: Record<string, number> = {},
): ShipmentLandedResult {
  const compliance: ComplianceRates = {
    arancelPctByItemId: arancelByItemId,
    ivaPctByItemId: ivaByItemId,
  };

  // FRONTERA SEGURA (B-02A): solo monto_base llega al motor.
  const engineGastos: EngineGastoInput[] = gastos.map((g) => {
    const capitalizable = Number(g.monto_base ?? 0);
    return {
      id: g.id,
      concepto: g.concepto,
      monto: capitalizable,
      moneda: g.moneda,
      base_prorrateo: g.base_prorrateo,
      trm_pago: g.trm_pago ?? null,
      aplica_costeo_inventario: g.aplica_costeo_inventario ?? true,
    };
  });

  const input: CostEngineInput = {
    documents,
    items,
    gastos: engineGastos,
    trmMap,
    trmAduana: trmAduana ?? null,
    compliance,
  };

  const result: CostEngineResult = calcLandedCostV2(input);

  const legacyItems: ItemProration[] = result.items.map((r) => ({
    itemId: r.itemId,
    monedaOrigen: r.monedaOrigen,
    factorValor: r.factorValor,
    factorPeso: r.factorPeso,
    fleteAsignado: r.gastosPesoAsignadosCOP,
    seguroAsignado: r.gastosValorAsignadosCOP,
    otrosAsignados: 0,
    gastosPesoAsignados: r.gastosPesoAsignadosCOP,
    gastosValorAsignados: r.gastosValorAsignadosCOP,
    cifItemCOP: r.cifItemCOP,
    arancelItemCOP: r.arancelItemCOP,
    ivaItemCOP: r.ivaItemCOP,
    landedCostTotalCOP: r.landedCostTotalCOP,
    landedCostUnitarioCOP: r.landedCostUnitarioCOP,
  }));

  return {
    engineVersion: result.engineVersion,
    reconciliationDriftCOP: result.reconciliationDriftCOP,
    warningFallbackPesoActivado: result.warningFallbackPesoActivado,
    warningPesoIncompleto: result.warningPesoIncompleto,
    itemsSinPeso: result.itemsSinPeso,
    totalLandedCOP: Number(result.globals.totalLandedCOP),
    globals: {
      fobTotalCOP: Number(result.globals.fobTotalCOP),
      pesoTotalKg: Number(result.globals.pesoTotalKg),
      gastosPesoCOP: Number(result.globals.gastosPesoCOP),
      gastosValorCOP: Number(result.globals.gastosValorCOP),
      // Legacy sync engine no separa CBM — siempre 0; usar el RPC async para el dato real.
      gastosCBMCOP: 0,
    },
    items: legacyItems,
    // Legacy sync API (calcLandedCostV2) no produce allocations granulares — usar el RPC async.
    allocations: [],
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// STANDALONE ASYNC HELPER (non-React) — used by dossier export, scripts, etc.
// Misma RPC, mismo adapter. NO depende de React Query.
// ─────────────────────────────────────────────────────────────────────────────

export interface FetchShipmentLandedCostArgs {
  orgId: string;
  documents: ShipmentDocument[];
  items: ShipmentItem[];
  gastos: ShipmentGasto[];
  trmAduana?: number | null;
  beneficioByItemId?: Record<string, "GENERAL" | "TLC_PURO" | "LEY_1715" | "ZONA_FRANCA">;
}

/**
 * Llama al RPC `calculate_landed_cost_preview` y devuelve el shape legacy
 * `ShipmentLandedResult` (incluye `allocations` granulares de TRACE-01).
 * Ideal para flujos no-React como `dossierExport.ts`.
 */
export async function fetchShipmentLandedCost(
  args: FetchShipmentLandedCostArgs,
): Promise<ShipmentLandedResult> {
  const { orgId, documents, items, gastos, trmAduana, beneficioByItemId } = args;
  if (!orgId) throw new Error("orgId requerido para fetchShipmentLandedCost");
  if (!items.length) {
    return {
      engineVersion: "v2",
      reconciliationDriftCOP: 0,
      warningFallbackPesoActivado: false,
      warningPesoIncompleto: false,
      itemsSinPeso: [],
      totalLandedCOP: 0,
      globals: { fobTotalCOP: 0, pesoTotalKg: 0, gastosPesoCOP: 0, gastosValorCOP: 0, gastosCBMCOP: 0 },
      items: [],
      allocations: [],
    };
  }

  const docMonedaById = new Map(documents.map((d) => [d.id, (d.moneda ?? "COP").toString()]));
  const payload: LandedCostPreviewInput = {
    trm_aduana: trmAduana && trmAduana > 0 ? Number(trmAduana) : (null as unknown as number),
    items: items.map((it) => {
      const beneficio = beneficioByItemId?.[it.id]
        ?? (it.aplica_tlc ? "TLC_PURO" : "GENERAL");
      return {
        id: it.id,
        documento_id: it.documento_id,
        moneda_doc: (docMonedaById.get(it.documento_id) ?? "COP").toUpperCase(),
        cantidad: Number(it.cantidad ?? 0),
        peso_bruto_kg: Number(it.peso_bruto_kg ?? 0),
        sub_total: Number(it.sub_total ?? 0),
        hs_code: (it as { hs_code?: string | null }).hs_code ?? null,
        aplica_tlc: !!it.aplica_tlc,
        iva_override: it.iva_override != null ? Number(it.iva_override) : null,
        beneficio_aduanero: beneficio,
      };
    }),
    gastos: gastos.map((g) => ({
      id: g.id,
      // Fase A — alinear con el hook React: usar helper canónico (monto_base
      // si existe, fallback a monto bruto) para gastos COP manuales sin IVA.
      monto: getMontoCapitalizable(g),
      moneda: g.moneda,
      // Fase C — CBM es ahora una bolsa nativa del RPC. Ya NO se colapsa a Valor.
      base_prorrateo: g.base_prorrateo,
      base_declarada: g.base_prorrateo,
      trm_pago: g.trm_pago ?? null,
      aplica_costeo_inventario: g.aplica_costeo_inventario ?? true,
    })),
  };

  const { data, error } = await supabase.rpc(
    "calculate_landed_cost_preview" as never,
    { p_org_id: orgId, p_input: payload as unknown as never } as never,
  );
  if (error) throw new Error(error.message);
  if (!data) throw new Error("RPC calculate_landed_cost_preview no devolvió datos.");
  const raw = data as unknown as LandedCostPreviewResult;
  const adapted = adaptRpcToLegacyShape(raw, items);

  try {
    // eslint-disable-next-line no-console
    console.groupCollapsed("🔍 [fetchShipmentLandedCost] RPC → adapter");
    console.table((raw.items ?? []).map((r) => ({
      itemId: r.id,
      "rpc.arancel_pct": r.arancel_pct,
      "rpc.iva_pct": r.iva_pct,
      "rpc.beneficio_aduanero": r.beneficio_aduanero,
      "rpc.arancel_lookup_pct": r.arancel_lookup_pct,
    })));
    console.table(adapted.items.map((r) => ({
      itemId: r.itemId,
      "adapter.arancelPctEfectivo": r.arancelPctEfectivo,
      "adapter.ivaPctEfectivo": r.ivaPctEfectivo,
    })));
    console.groupEnd();
  } catch (e) {
    console.warn("[fetchShipmentLandedCost] diag log failed", e);
  }

  return adapted;
}
